import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enTranslations from "./locales/en.json";
import ptTranslations from "./locales/pt.json";
import frTranslations from "./locales/fr.json";
import esTranslations from "./locales/es.json";
import itTranslations from "./locales/it.json";

import type { TenantConfig } from "../tenant/tenants.config";

// ---------------------------------------------------------------------------
// Deep-merge utility: recursively merge `source` into `target`.
// `source` values win when both sides have a leaf value.
// ---------------------------------------------------------------------------
function deepMerge(
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    const srcVal = source[key];
    const tgtVal = result[key];
    if (
      srcVal &&
      typeof srcVal === "object" &&
      !Array.isArray(srcVal) &&
      tgtVal &&
      typeof tgtVal === "object" &&
      !Array.isArray(tgtVal)
    ) {
      result[key] = deepMerge(
        tgtVal as Record<string, unknown>,
        srcVal as Record<string, unknown>
      );
    } else {
      result[key] = srcVal;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Initialise i18next with base resources + tenant copy overrides.
// Must be called AFTER tenant resolution and BEFORE the first React render.
// ---------------------------------------------------------------------------
export function initI18n(tenantConfig: TenantConfig): void {
  const tenantCopy = tenantConfig.copy ?? {};

  const enMerged = tenantCopy.en
    ? deepMerge(
        enTranslations as Record<string, unknown>,
        tenantCopy.en as Record<string, unknown>
      )
    : enTranslations;

  const ptMerged = tenantCopy.pt
    ? deepMerge(
        ptTranslations as Record<string, unknown>,
        tenantCopy.pt as Record<string, unknown>
      )
    : ptTranslations;

  const frMerged = tenantCopy.fr
    ? deepMerge(
        frTranslations as Record<string, unknown>,
        tenantCopy.fr as Record<string, unknown>
      )
    : frTranslations;

  const esMerged = tenantCopy.es
    ? deepMerge(
        esTranslations as Record<string, unknown>,
        tenantCopy.es as Record<string, unknown>
      )
    : esTranslations;

  const itMerged = tenantCopy.it
    ? deepMerge(
        itTranslations as Record<string, unknown>,
        tenantCopy.it as Record<string, unknown>
      )
    : itTranslations;

  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      detection: {
        order: ["localStorage", "navigator"],
        lookupLocalStorage: "i18nextLng",
        caches: ["localStorage"],
      },
      fallbackLng: "en",
      supportedLngs: ["en", "pt", "fr", "es", "it"],
      resources: {
        en: {
          translation: enMerged,
        },
        pt: {
          translation: ptMerged,
        },
        fr: {
          translation: frMerged,
        },
        es: {
          translation: esMerged,
        },
        it: {
          translation: itMerged,
        },
      },
      interpolation: {
        escapeValue: false,
      },
      debug: false,
    });
}

export default i18n;
